import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from 'src/components/ui/Icon/Icon';
import FlowAiOrb from 'src/components/ui/FlowAiOrb/FlowAiOrb';
import AiChatPanel from 'src/components/layouts/AiChat/AiChat';
import { useRouteAccess } from 'src/hooks/useRouteAccess';
import styles from './FlowAiWidget.module.scss';

const OPEN_KEY = 'flowAiWidgetOpen';
const CLOSE_DURATION = 350;

const FlowAiWidget: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasAccess } = useRouteAccess();
  const [open, setOpen] = useState(() => sessionStorage.getItem(OPEN_KEY) === '1');
  const [closing, setClosing] = useState(false);
  const closeTimer = useRef<number>();

  useEffect(() => {
    sessionStorage.setItem(OPEN_KEY, open ? '1' : '0');
  }, [open]);

  const close = () => {
    if (!open || closing) return;
    setClosing(true);
    closeTimer.current = window.setTimeout(() => {
      setClosing(false);
      setOpen(false);
    }, CLOSE_DURATION);
  };

  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, closing]);

  if (!hasAccess('ai-chat') || location.pathname.startsWith('/ai-chat')) {
    return null;
  }

  // Keeps the page end free under the fixed launcher; also while the panel is open, so the page does not jump.
  const spacer = <div className={styles.spacer} aria-hidden />;

  if (open) {
    return (
      <>
        {spacer}
        <div
          className={classNames(styles.panel, closing && styles.closing)}
          role="dialog"
          aria-label={t('ai_widget.title')}>
          <div className={styles.head}>
            <FlowAiOrb size={40} />
            <div className="flex flex-col min-w-0 flex-1">
              <span className={styles.name}>{t('ai_widget.title')}</span>
              <span className={styles.status}>
                <span className={styles.statusDot} />
                {t('ai_widget.status_online')}
              </span>
            </div>
            <div className={styles.headButtons}>
              <button
                type="button"
                className={styles.headButton}
                onClick={() => {
                  setOpen(false);
                  navigate('/ai-chat');
                }}
                aria-label={t('ai_widget.fullscreen')}
                title={t('ai_widget.fullscreen')}>
                <Icon name="openInNew" width={16} height={16} />
              </button>
              <button
                type="button"
                className={styles.headButton}
                onClick={close}
                aria-label={t('ai_widget.minimize')}
                title={t('ai_widget.minimize')}>
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round">
                  <path d="M6 12h12" />
                </svg>
              </button>
            </div>
          </div>
          <div className={styles.body}>
            <AiChatPanel compact />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {spacer}
      <button
        type="button"
        className={styles.launcher}
        onClick={() => setOpen(true)}
        aria-label={t('ai_widget.open')}
        title={t('ai_widget.open')}>
        <FlowAiOrb size={56} />
      </button>
    </>
  );
};

export default FlowAiWidget;
